import { Box, Container, Heading, Text, VStack, SimpleGrid, List, ListItem, ListIcon, Button } from "@chakra-ui/react";
import { FaCheckCircle, FaBolt, FaMoneyBillWave, FaNetworkWired } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const VPPBatteriesPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <NavbarWithCallToAction />
      <Box bg={getColor("background.main")} minHeight="100vh">
        <Box position="relative" width="100vw" height="500px" overflow="hidden" marginLeft="50%" transform="translateX(-50%)" backgroundImage="url('/assets/onesolarimages/abouthero.jpg')" backgroundSize="cover" backgroundPosition="center">
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.5)" zIndex={1} />
          <Container maxW="1200px" position="relative" zIndex={2} height="100%" display="flex" alignItems="center">
            <VStack spacing={6} align="flex-start" maxW="800px">
              <Heading as="h1" fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }} fontWeight="bold" color="white" textShadow="2px 2px 4px rgba(0,0,0,0.5)" fontFamily="'Open Sans', sans-serif" textTransform="uppercase">Virtual Power Plants, Batteries & Off Grid Systems</Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} color="white" textShadow="2px 2px 4px rgba(0,0,0,0.5)" fontFamily="'Open Sans', sans-serif">Get the Best Solar Feed-in Rates for Your Excess Power</Text>
              <Button size="lg" bg="white" color="#121c47" px={8} py={6} fontSize="md" fontWeight="600" textTransform="uppercase" borderRadius="4px" fontFamily="'Open Sans', sans-serif" _hover={{ bg: "gray.100" }} onClick={() => navigate("/contact")}>Learn More</Button>
            </VStack>
          </Container>
        </Box>

        <Box bg="white" py={{ base: 16, md: 20 }}>
          <Container maxW="1200px">
            <VStack spacing={16} align="stretch">
              <Box>
                <Box width="60px" height="2px" bg="#121c47" mb={4} />
                <Heading as="h2" fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6}>Maximize Your Solar Investment</Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={4}>After solar installation, you can optimize your savings through superior purchase and resale rates. Our consultants help you explore energy provider alternatives, potential Virtual Power Plant participation, and battery aggregation for premium power sales.</Text>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" fontWeight="600">Stop giving away money to the big providers and ask how you can save.</Text>
              </Box>

              <Box bg="#f8f9fa" p={{ base: 6, md: 8 }} borderRadius="lg">
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6} textAlign="center">Premium Rates Available</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} textAlign="center">
                  <Box>
                    <Text fontSize="4xl" fontWeight="bold" color="#121c47" mb={2}>45¢</Text>
                    <Text fontSize="lg" fontWeight="600" color="#050303">Feed-in Rate</Text>
                    <Text fontSize="sm" color="#666">per kW - up to twice the going rate</Text>
                  </Box>
                  <Box>
                    <Text fontSize="4xl" fontWeight="bold" color="#121c47" mb={2}>19¢</Text>
                    <Text fontSize="lg" fontWeight="600" color="#050303">Peak Usage Rate</Text>
                    <Text fontSize="sm" color="#666">per kW - competitive pricing</Text>
                  </Box>
                  <Box>
                    <Text fontSize="4xl" fontWeight="bold" color="#121c47" mb={2}>85¢</Text>
                    <Text fontSize="lg" fontWeight="600" color="#050303">Supply Charge</Text>
                    <Text fontSize="sm" color="#666">per day - low fixed cost</Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Box>
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6}>What is a Virtual Power Plant (VPP)?</Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={4}>VPPs challenge traditional centralized power plant models by combining distributed energy resources across multiple locations. They utilize software-based smart grid technology to provide continuous, reliable 24-hour power supply through advanced planning, scheduling, and bidding mechanisms.</Text>
              </Box>

              <Box>
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" color="#050303" fontFamily="'Open Sans', sans-serif" mb={6}>Benefits of VPP & Battery Systems</Heading>
                <List spacing={4}>
                  {[
                    'Sell excess power at premium rates - up to twice standard feed-in tariffs',
                    'Store solar energy for use during peak pricing periods',
                    'Participate in grid stabilization programs for additional revenue',
                    'Reduce reliance on grid electricity during expensive peak hours',
                    'Contribute to a more sustainable and resilient power grid',
                    '24/7 power availability with battery backup systems'
                  ].map((item, i) => (
                    <ListItem key={i}>
                      <ListIcon as={FaCheckCircle} color="#121c47" />
                      <Text as="span" fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">{item}</Text>
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box bg="#121c47" color="white" p={{ base: 8, md: 12 }} borderRadius="lg" textAlign="center">
                <Heading as="h3" fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold" fontFamily="'Open Sans', sans-serif" mb={4}>Ready to Maximize Your Solar Returns?</Heading>
                <Text fontSize={{ base: "md", md: "lg" }} fontFamily="'Open Sans', sans-serif" mb={6}>Contact us today to learn how VPP and battery systems can increase your savings and energy independence.</Text>
                <Button size="lg" bg="white" color="#121c47" px={8} py={6} fontSize="md" fontWeight="600" textTransform="uppercase" borderRadius="4px" fontFamily="'Open Sans', sans-serif" _hover={{ bg: "gray.100" }} onClick={() => navigate("/contact")}>Get Started</Button>
              </Box>
            </VStack>
          </Container>
        </Box>
        <FooterWithFourColumns />
      </Box>
    </>
  );
};

export default VPPBatteriesPage;
